import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
  type Router,
  type RouterScrollBehavior,
} from 'vue-router'
import { handleVerifyToken, handleCodeExchange, handleResetToken } from '@/composables/useAuthTokens'
import { trackMetaPixelPageView } from '@/analytics/metaPixel'
import {
  BRAND_DETAILS_ROUTE,
  BRANDS_ROUTE,
  CAPSULE_ROUTE,
  FAVORITES_ROUTE,
  HOME_ALIASES,
  HOME_ROUTE,
  LISTS_ROUTE,
  PRIVACY_POLICY_ROUTE,
  PRODUCT_ROUTE,
  PRODUCT_SIMILAR_ROUTE,
  PROFILE_ROUTE,
  PROFILE_PERSONAL_DATA_ROUTE,
  PROFILE_SETTINGS_ROUTE,
  PROFILE_NOTIFICATIONS_ROUTE,
  PROFILE_NOTIFICATIONS_LIST_ROUTE,
  PROFILE_MY_PARAMETERS_ROUTE,
  PROFILE_SIZES_ROUTE,
  PROFILE_MY_STYLE_ROUTE,
  PROFILE_PEOPLE_ROUTE,
  RECOMMENDATION_BANNERS_ROUTE,
  GIFT_IDEAS_ROUTE,
  RECOMMENDATION_COLLECTIONS_ROUTE,
  RESET_PASSWORD_ROUTE,
  SEARCH_ROUTE,
  STYLE_SPACE_ROUTE,
  TERMS_OF_SERVICE_ROUTE,
  PAGE_LOADER_PREVIEW_ROUTE,
  RIVE_LOADER_PREVIEW_ROUTE,
  VERIFY_ROUTE
} from '@/router/routes'
import { useTokenStore, useAuthModal } from '@/features/auth/store'
import {
  fairBrandsV2LetterSectionId as brandLetterSectionId,
  normalizeFairBrandsV2Letter as normalizeFairBrandLetter,
} from '@/widgets/brands/fair-v2/fair-brands-v2-catalog.data'
import { applyFairSeo } from '@/composables/useFairSeo'
import { buildFairSearchSeo, getFairRouteSeo } from '@/seo/routeSeo'
import { shouldStartFairPageLoader } from '@/features/page-loader/fairPageLoader.guards'
import { useFairPageLoaderStore } from '@/features/page-loader/store'

/**
 * A sign-in-only route for a guest (T-016): the auth modal opens right where she is — no
 * navigation, no page loader — and once signed in she is taken where she was going. Only a
 * cold deep link (nothing to stay on) falls back to Home under the modal.
 */
function requireSignIn(
  router: Router,
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): true | false | RouteLocationRaw {
  if (useTokenStore().isAuthenticated()) return true
  if (typeof window === 'undefined') return { name: 'home' }
  const target = to.fullPath
  useAuthModal().showAuthModal('signIn', { then: () => void router.push(target) })
  return from.matched.length > 0 ? false : { name: 'home' }
}

export const routes: RouteRecordRaw[] = [
  {
    path: HOME_ROUTE,
    name: 'home',
    component: () => import('@/pages/HomePage.vue'),
    alias: HOME_ALIASES,
    meta: { pageLoader: true },
  },
  {
    path: VERIFY_ROUTE,
    name: 'verify-user',
    component: () => import('@/pages/HomePage.vue'),
    beforeEnter: async (to) => {
      await handleVerifyToken(to.params.verifyToken as string)
      return { name: 'home' }
    }
  },
  {
    path: RESET_PASSWORD_ROUTE,
    name: 'reset-password',
    component: () => import('@/pages/HomePage.vue'),
    beforeEnter: (to) => {
      handleResetToken(to.params.resetToken as string)
      return { name: 'home' }
    }
  },
  {
    path: PAGE_LOADER_PREVIEW_ROUTE,
    name: 'page-loader-preview',
    component: () => import('@/pages/FairPageLoaderPreviewPage.vue'),
  },
  {
    path: RIVE_LOADER_PREVIEW_ROUTE,
    name: 'rive-loader-preview',
    component: () => import('@/pages/FairRiveLoaderPreviewPage.vue'),
  },
  {
    path: BRANDS_ROUTE,
    name: 'brands',
    component: () => import('@/pages/FairBrandsPage.vue')
  },
  {
    path: BRAND_DETAILS_ROUTE,
    name: 'brand-details',
    component: () => import('@/pages/FairBrandDetailPage.vue'),
    props: true,
    meta: { pageLoader: true },
  },
  {
    path: PROFILE_ROUTE,
    name: 'profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_SETTINGS_ROUTE,
    name: 'profile-settings',
    component: () => import('@/pages/profile/SettingsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_PERSONAL_DATA_ROUTE,
    name: 'profile-personal-data',
    component: () => import('@/pages/profile/PersonalDataPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_NOTIFICATIONS_ROUTE,
    name: 'profile-notifications',
    component: () => import('@/pages/profile/NotificationSettingsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_NOTIFICATIONS_LIST_ROUTE,
    name: 'profile-notifications-list',
    component: () => import('@/pages/profile/NotificationsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    // The redesign folded measurements into /profile/sizes (plan §6.6); the
    // old path stays for links already out in the wild.
    path: PROFILE_MY_PARAMETERS_ROUTE,
    name: 'profile-my-parameters',
    redirect: { name: 'profile-sizes', hash: '#measurements' },
  },
  {
    path: PROFILE_SIZES_ROUTE,
    name: 'profile-sizes',
    component: () => import('@/pages/profile/SizesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_MY_STYLE_ROUTE,
    name: 'profile-my-style',
    component: () => import('@/pages/profile/MyStylePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: PROFILE_PEOPLE_ROUTE,
    name: 'profile-people',
    component: () => import('@/pages/profile/PeoplePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: FAVORITES_ROUTE,
    name: 'favorites',
    // FE-027 — the redesign Favourites; the legacy `FavoritesPage.vue` stays for the profile shell.
    component: () => import('@/widgets/saved/fair-v2/FairSavedV2Page.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: LISTS_ROUTE,
    name: 'lists',
    // FE-028 — the redesign Lists.
    component: () => import('@/widgets/saved/fair-v2/FairSavedV2Page.vue'),
    meta: { requiresAuth: true }
  },
  {
    // FE-029 — one list.
    path: `${LISTS_ROUTE}/:listId(\\d+)`,
    name: 'list-details',
    component: () => import('@/widgets/saved/fair-v2/FairListV2Page.vue'),
    meta: { requiresAuth: true, pageLoader: true },
  },
  {
    path: CAPSULE_ROUTE,
    name: 'capsule',
    component: () => import('@/pages/CapsulePageTest.vue'),
    meta: { pageLoader: true },
  },
  {
    path: RECOMMENDATION_BANNERS_ROUTE,
    name: 'recommendation-banners',
    component: () => import('@/pages/FairSelectionPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: GIFT_IDEAS_ROUTE,
    name: 'gift-ideas',
    component: () => import('@/pages/FairGiftIdeasPage.vue'),
  },
  {
    path: RECOMMENDATION_COLLECTIONS_ROUTE,
    name: 'recommendation-collections',
    component: () => import('@/features/recommendations/ui/CollectionsList.vue')
  },
  {
    path: SEARCH_ROUTE,
    name: 'search',
    component: () => import('@/pages/SearchPage.vue'),
    props: route => ({ searchText: route.params.searchText ?? '' }),
    meta: { pageLoader: true },
  },
  {
    path: STYLE_SPACE_ROUTE,
    name: 'style-space',
    component: () => import('@/pages/FairStyleSpacePage.vue'),
    meta: { pageLoader: true, styleSpaceAuth: true },
    // Every /playground/* route wants a JWT: a guest gets the auth modal and
    // lands on Home, the way the persona sections treat a guest there.
  },
  {
    // The AIRA canvas is a route of its own now (redesign); `?v1=1` used to stand in for it.
    path: `${STYLE_SPACE_ROUTE}/playground`,
    name: 'style-space-playground',
    component: () => import('@/pages/FairStyleSpacePlaygroundPage.vue'),
    meta: { pageLoader: true, styleSpaceAuth: true },
  },
  {
    path: `${STYLE_SPACE_ROUTE}/outfits/:outfitId(\\d+)`,
    name: 'style-space-outfit',
    component: () => import('@/pages/FairStyleSpaceOutfitPage.vue'),
    meta: { pageLoader: true, styleSpaceAuth: true },
  },
  {
    path: `${STYLE_SPACE_ROUTE}/capsules/:capsuleId(\\d+)`,
    name: 'style-space-capsule',
    component: () => import('@/pages/FairStyleSpaceCapsulePage.vue'),
    meta: { pageLoader: true, styleSpaceAuth: true },
  },
  {
    path: PRODUCT_SIMILAR_ROUTE,
    name: 'product-similar',
    component: () => import('@/pages/FairProductSimilarPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: PRODUCT_ROUTE,
    name: 'product',
    component: () => import('@/pages/ProductPage.vue'),
    meta: { pageLoader: true },
  },
  {
    path: PRIVACY_POLICY_ROUTE,
    name: 'privacy-policy',
    component: () => import('@/pages/PrivacyPolicyPage.vue')
  },
  {
    path: TERMS_OF_SERVICE_ROUTE,
    name: 'terms-of-service',
    component: () => import('@/pages/TermsOfServicePage.vue')
  },
  // Dev-only preview of the subscriptions paywall — not registered in production builds.
  ...(import.meta.env.DEV
    ? [{
        path: '/dev/paywall',
        name: 'dev-paywall',
        component: () => import('@/pages/dev/DevPaywallPage.vue'),
      }]
    : []),
]

function resolveBrandsLetterFromQuery(
  letter: unknown,
): string {
  if (Array.isArray(letter)) {
    return normalizeFairBrandLetter(letter[0])
  }
  return normalizeFairBrandLetter(typeof letter === 'string' ? letter : '')
}

async function scrollToBrandsLetterElement(
  letter: string,
  behavior: ScrollBehavior = 'smooth',
): Promise<boolean> {
  if (typeof document === 'undefined') return false
  const normalized = normalizeFairBrandLetter(letter)
  if (!normalized) return false
  const id = brandLetterSectionId(normalized)

  for (let attempt = 0; attempt < 24; attempt += 1) {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior, block: 'start' })
      return true
    }
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve())
    })
  }
  return false
}

/**
 * T-104 — a size or colour pick on the product page rewrites the address (`router.replace`) but
 * stays on the same page; scrolling to the top there read as «another product opened». The page
 * calls this right before such a replace, and that one navigation keeps the scroll.
 */
let keepScrollOnce = false

export function keepScrollOnNextNavigation(): void {
  keepScrollOnce = true
}

export const scrollBehavior: RouterScrollBehavior = (to, _from, savedPosition) => {
  if (keepScrollOnce) {
    keepScrollOnce = false
    return false
  }

  if (savedPosition) {
    return savedPosition
  }

  if (typeof document === 'undefined') {
    return { top: 0 }
  }

  if (to.hash) {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        const element = document.querySelector(to.hash)
        if (element) {
          element.scrollIntoView({ behavior: 'auto', block: 'start' })
        }
        resolve()
      })
    }).then(() => {
      if (document.querySelector(to.hash)) {
        return { el: to.hash, behavior: 'auto' as ScrollBehavior }
      }
      return { top: 0 }
    })
  }

  if (to.name === 'brands') {
    const letter = resolveBrandsLetterFromQuery(to.query.letter)
    if (letter) {
      return scrollToBrandsLetterElement(letter, 'smooth').then(() => false)
    }
  }

  return { top: 0, behavior: 'auto' as ScrollBehavior }
}

/** Auth + SEO guards — attach to ViteSSG router or standalone factory. */
export function registerRouterGuards(router: Router): void {
  router.beforeEach(async (to, from) => {
    const tokenStore = useTokenStore()

    if (to.query.code && !from.query.code) {
      await handleCodeExchange(to.query.code as string)
      const query = { ...to.query }
      delete query.code
      return { path: to.path, query, hash: to.hash }
    }

    // T-016 — before the page loader: a guest gets the auth modal at once, on the same page.
    if (to.meta.requiresAuth || to.meta.styleSpaceAuth) {
      const allowed = requireSignIn(router, to, from)
      if (allowed !== true) return allowed
    }

    if (typeof window !== 'undefined') {
      const pageLoader = useFairPageLoaderStore()
      if (shouldStartFairPageLoader(to, from)) {
        pageLoader.start()
      } else if (pageLoader.isActive && !to.meta.pageLoader) {
        pageLoader.fail()
      }
    }

    return true
  })

  router.afterEach((to, from) => {
    trackMetaPixelPageView(to, from)
    if (to.name === 'search') {
      applyFairSeo(buildFairSearchSeo(to))
      return
    }
    applyFairSeo(getFairRouteSeo(to.name, to.path))
  })

  router.onError(() => {
    if (typeof window !== 'undefined') {
      useFairPageLoaderStore().fail()
    }
  })
}

let activeRouter: Router | null = null

/** Bind the live app router (ViteSSG instance) for legacy `import router from '@/router'`. */
export function setActiveRouter(router: Router): void {
  activeRouter = router
}

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior,
  })
  registerRouterGuards(router)
  setActiveRouter(router)
  return router
}

function getActiveRouter(): Router {
  if (!activeRouter) {
    return createAppRouter()
  }
  return activeRouter
}

const router = new Proxy({} as Router, {
  get(_target, prop, receiver) {
    const instance = getActiveRouter()
    const value = Reflect.get(instance, prop, receiver)
    return typeof value === 'function' ? value.bind(instance) : value
  },
})

export default router
